import React, { useCallback, useState, useEffect } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  TextField,
  MenuItem,
  Chip,
} from "@mui/material";
import GroupAddIcon from "@mui/icons-material/GroupAdd";
import CancelIcon from "@mui/icons-material/Cancel";
import EditIcon from "@mui/icons-material/Edit";
import SingleImageUpload from "../../../../../components/UploadImage/UploadImage";
import toast from "react-hot-toast";
import {
  levelsOptions,
  normalizeValue,
  trainingTypesOptions,
  daysOfWeekOptions,
} from "./handleTime";
import DayScheduleSelector from "./DayScheduleSelector";
import MapRunningClubProvider from "../MapCreateRunningClub";
import {
  createRunningClub,
  getAllRunningsClubsOfProvider,
  updateRunning,
} from "../../../../../services/rest/runningsClubs/runningClubsProviderService";
import { handleError } from "../../../../../utils/handleError";
import { useDispatch } from "react-redux";
import { setRunningsClubsOfCoach } from "../../../../../redux/slice/runningsClubsSlice";
import ComunityRadaria from "../../../../../assets/images/ComunidadDeRadaria-Aux.webp";

const ModalClubForm = ({ open, setOpen, setLoading, clubToEdit }) => {
  const [imageSelected, setImageSelected] = useState(null);
  const [trainingDays, setTrainingDays] = useState([]);
  const [level, setLevel] = useState("");
  const [typeTraining, setTypeTraining] = useState("");
  const [nameRunningClub, setNameRunningClub] = useState("");
  const [urlInstagram, setUrlInstagram] = useState("");
  const [location, setLocation] = useState([]);
  const [descriptionLocation, setDescriptionLocation] = useState("");
  const dispatch = useDispatch();

  // Inicializar los valores si estamos editando
  useEffect(() => {
    if (clubToEdit) {
      setNameRunningClub(clubToEdit?.nameRunningClub || "");
      setUrlInstagram(clubToEdit?.urlInstagram || "");
      setTrainingDays(clubToEdit?.trainingDays || []);
      setLevel(normalizeValue(clubToEdit?.level, levelsOptions));
      setTypeTraining(
        normalizeValue(clubToEdit?.typeTraining, trainingTypesOptions)
      );
      setDescriptionLocation(clubToEdit?.descriptionLocation ?? "");
      setLocation(clubToEdit?.location ?? []);
    } else {
      // Resetear los valores si estamos creando
      setNameRunningClub("");

      setUrlInstagram("");
      setTrainingDays([]);
      setLevel("");
      setTypeTraining("");
      setImageSelected(null);
      setLocation([]);
    }
  }, [clubToEdit, open]);

  const handleClose = useCallback(() => {
    setOpen(false);
    setImageSelected(null);
    setDescriptionLocation("");
  }, [setOpen]);

  const handleImageChange = (imageFile) => {
    setImageSelected(imageFile);
  };

  const handleAddDaySchedule = (newDaySchedule) => {
    setTrainingDays((prev) => [...prev, newDaySchedule]);
  };

  const handleRemoveDaySchedule = (index) => {
    setTrainingDays((prev) => prev.filter((_, i) => i !== index));
  };

  const saveCommunity = async (event) => {
    try {
      event.preventDefault();
      setLoading(true);
      if (trainingDays.length === 0) {
        setLoading(false);
        toast.error("Seleccione por lo menos un día de entrenamiento.");
        return;
      } else if (descriptionLocation === "" || location.length === 0) {
        setLoading(false);
        toast.error("Seleccione la ubicación de entrenamientos.");
        return;
      }
      //  if (clubToEdit?.urlImg) {
      //   setLoading(false);
      //   toast.error("Seleccione su imagen de perfil.");
      //   return;
      // }

      const formData = new FormData(event.currentTarget);
      if (imageSelected) {
        formData.append("img", imageSelected);
      }

      if (clubToEdit) {
        formData.append("_id", clubToEdit?._id);
        formData.delete("nameRunningClub");
      }

      if (location && location.length > 0) {
        formData.append("location", JSON.stringify(location));
      }
      if (trainingDays.length > 0) {
        formData.append("trainingDays", JSON.stringify(trainingDays));
      }
      // const dataRunningClub = Object.fromEntries(formData.entries());

      // console.log("FormData", formData);

      // Aquí hacer la petición a la API
      clubToEdit
        ? await updateRunning(clubToEdit?._id, formData)
        : await createRunningClub(formData);

      const listResponseClubs = await getAllRunningsClubsOfProvider();
      dispatch(
        setRunningsClubsOfCoach({
          runningsClubsOfCoach: listResponseClubs ?? [],
        })
      );
      handleClose();
      toast.success(
        clubToEdit
          ? "Club actualizado correctamente"
          : "Se agregó correctamente la comunidad"
      );
      setLoading(false);
      setDescriptionLocation("");
    } catch (error) {
      setLoading(false);
      const { message } = handleError(error);
      // console.log("Error en Save Community", error);
      toast.error(
        `En este momento no se puede ${clubToEdit ? "actualizar" : "crear"} tu comunidad. \n ${message}`
      );
    }
  };

  // Función para formatear el día y horarios para mostrar en los chips
  const formatDaySchedule = (daySchedule) => {
    const dayOption = daysOfWeekOptions.find(
      (d) => d.value === daySchedule.day
    );
    const dayName = dayOption ? dayOption.label : daySchedule.day;
    const formattedTimes = daySchedule.times.map((time) => {
      const [hours, minutes] = time.split(":");
      return `${hours.padStart(2, "0")}:${minutes}`;
    });
    return `${dayName}: ${formattedTimes.join(", ")}`;
  };

  return (
    <Dialog
      open={open}
      slotProps={{
        paper: {
          component: "form",
          onSubmit: (event) => {
            saveCommunity(event);
          },
        },
      }}
      maxWidth="lg"
    >
      <DialogContent sx={{ gap: 2 }}>
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", md: "row" }, // Columna en móvil, fila en desktop
            gap: 4,
            alignItems: { xs: "center", md: "flex-start" },
          }}
        >
          {/* Contenedor del formulario */}
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 3,
              width: { xs: "100%", md: "auto" }, // Ancho completo en móvil, auto en desktop
            }}
          >
            {/* {clubToEdit?.urlImg && clubToEdit?.urlImg !== "" && (
          <Box sx={{ borderRadius: 5, overflow: "hidden" }}>
            <img
              src={clubToEdit?.urlImg ?? ComunityRadaria}
              width={150}
              alt="imagen del club."
            />
          </Box>
        )} */}
            <Box>
              <SingleImageUpload
                onImageChange={handleImageChange}
                initialImage={clubToEdit?.urlImg}
              />
            </Box>

            {/* Grupo de campos de texto */}
            <Box
              sx={{
                display: "flex",
                flexDirection: { xs: "column", sm: "row" }, // Responsive para los campos
                gap: 4,
                marginTop: 4,
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 3,
                  flex: 1,
                }}
              >
                <TextField
                  autoFocus
                  required
                  id="nameRunningClub"
                  name="nameRunningClub"
                  label="Nombre del Club"
                  fullWidth
                  variant="outlined"
                  value={nameRunningClub}
                  disabled={clubToEdit}
                  onChange={(e) => setNameRunningClub(e.target.value)}
                />
                <TextField
                  select
                  required
                  id="typeTraining"
                  name="typeTraining"
                  label="Tipo de Entrenamientos"
                  fullWidth
                  variant="outlined"
                  value={typeTraining}
                  onChange={(e) => setTypeTraining(e.target.value)}
                >
                  {trainingTypesOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </TextField>
              </Box>

              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 3,
                  flex: 1,
                }}
              >
                <TextField
                  select
                  required
                  id="level"
                  name="level"
                  label="Nivel de corredores"
                  fullWidth
                  variant="outlined"
                  value={level}
                  onChange={(e) => setLevel(e.target.value)}
                >
                  {levelsOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </TextField>

                <TextField
                  autoFocus
                  required
                  id="urlInstagram"
                  name="urlInstagram"
                  label="Perfil de instagram (URL)"
                  fullWidth
                  variant="outlined"
                  value={urlInstagram}
                  onChange={(e) => setUrlInstagram(e.target.value)}
                />
              </Box>
            </Box>

            {/* Selector de horarios */}
            <Box
              sx={{
                display: "flex",
                gap: 4,
                flexDirection: "column",
                justifyContent: "center",
              }}
            >
              <DayScheduleSelector
                onAddDaySchedule={handleAddDaySchedule}
                daysOfWeekOptions={daysOfWeekOptions}
                existingDays={trainingDays}
              />
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mt: 2 }}>
                {trainingDays.map((daySchedule, index) => (
                  <Chip
                    key={index}
                    label={formatDaySchedule(daySchedule)}
                    onDelete={() => handleRemoveDaySchedule(index)}
                  />
                ))}
              </Box>
            </Box>
          </Box>

          {/* Mapa - se moverá abajo en móvil */}
          <Box
            sx={{
              width: { xs: "100%", md: 500 },
              height: { xs: 400, md: 500 },
              mt: { xs: 2, md: 0 }, // Margen superior solo en móvil
            }}
          >
            <MapRunningClubProvider
              runningClub={clubToEdit}
              location={location}
              setLocation={setLocation}
              descriptionLocation={descriptionLocation}
              setDescriptionLocation={setDescriptionLocation}
              setLoading={setLoading}
            />
          </Box>
        </Box>
      </DialogContent>
      <DialogActions>
        <Box sx={{ display: "flex", gap: 2 }}>
          <Button
            variant="outlined"
            sx={{ borderColor: "white", color: "white" }}
            onClick={handleClose}
            startIcon={<CancelIcon />}
          >
            Cerrar
          </Button>
          <Button
            variant="contained"
            type="submit"
            sx={{
              backgroundColor: "#e7ef7d",
              "&:hover": {
                backgroundColor: "#d4dc6d",
              },
            }}
            startIcon={clubToEdit ? <EditIcon /> : <GroupAddIcon />}
          >
            {clubToEdit ? "Actualizar" : "Crear"}
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
};

export default ModalClubForm;
